import { z } from "zod";

/**
 * What KIND of call a model answers — the interface it speaks, not how well.
 *
 * - `generate` — takes a prompt and produces content: free text, or an object a
 *   JSON schema describes. Every generative vendor model is this.
 * - `classify` — takes content and a set of typed questions (yes/no, one of N
 *   options, a level on an ordered scale) and returns a calibrated probability
 *   per question. It cannot produce free text at all, so a schema with a
 *   free-text field, a chat turn, or a tool call is not a request it can serve.
 *
 * Every surface that decides whether a model may be used somewhere reads THIS,
 * never the model's provider: a provider is a routing fact (which adapter, which
 * credential), and a vendor is free to ship a model of either kind. A picker, a
 * save-time validator or a server path that asked "is the provider X" would be
 * wrong the day that vendor ships its second kind of model, and silently so.
 *
 * Declared as an `as const` array with the union derived from it — the runtime
 * list is what lets a `Record<ModelCapability, …>` consumer be asserted
 * exhaustive in a test rather than restated by hand.
 */
export const MODEL_CAPABILITIES = ["generate", "classify"] as const;

export type ModelCapability = (typeof MODEL_CAPABILITIES)[number];

export const ModelCapabilitySchema = z.enum(MODEL_CAPABILITIES);

/**
 * The capability of a catalog entry that does not declare one. Every model the
 * catalog carried before `classify` existed is generative, so absence means
 * `generate` — and only absence: a declared value is always read as written.
 */
export const DEFAULT_MODEL_CAPABILITY: ModelCapability = "generate";

/**
 * The capability of a catalog entry, with the default applied. Accepts `null`
 * because `getModel` returns it for an id the catalog does not carry; such a
 * model is treated as generative, which is what every call path assumed before
 * this field existed.
 */
export function modelCapabilityOf(
  entry: { readonly capability?: ModelCapability } | null | undefined
): ModelCapability {
  return entry?.capability ?? DEFAULT_MODEL_CAPABILITY;
}
